import React from 'react';
import { SitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { Route, Switch } from 'react-router-dom';
import { ApolloProvider } from 'react-apollo';
import componentFactory from './temp/componentFactory';
import RouteHandler from './RouteHandler';
import { ThemeProvider } from '@mui/material';
import MUITheme from './lib/MUITheme';
import { withTranslation } from 'react-i18next';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';

// This is the main JSX entry point of the app invoked by the renderer (server or client rendering).
// By default the app's normal rendering is delegated to <RouteHandler> that handles the loading of JSS route data.

// support languages in the URL prefix
// e.g. /da-DK/path, or /en/path, or /path
export const routePatterns = [
  '/:lang([a-z]{2}-[A-Z]{2})/:sitecoreRoute*',
  '/:lang([a-z]{2})/:sitecoreRoute*',
  '/:sitecoreRoute*',
];

// wrap the app with:
// ApolloProvider: provides an instance of Apollo GraphQL client to the app to make Connected GraphQL queries.
//    Not needed if not using connected GraphQL.
// SitecoreContext: provides component resolution and context services via withSitecoreContext
// Router: provides a basic routing setup that will resolve Sitecore item routes and allow for language URL prefixes.
class AppRoot extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      ssrRenderComplete: false,
    };

    if (props.ssrState) {
      this.sitecoreContext =
        props.ssrState.sitecore && props.ssrState.sitecore.route
          ? {
              ...props.ssrState.sitecore.context,
              route: props.ssrState.sitecore.route,
              itemId: props.ssrState.sitecore.route.itemId,
            }
          : props.ssrState.sitecore.context;
    } else {
      this.sitecoreContext = null;
    }
  }

  setSsrRenderComplete(ssrRenderComplete) {
    this.setState({
      ssrRenderComplete,
    });
  }

  componentDidMount() {
    this.setSsrRenderComplete(true);

    //  tracking code
    try {
      window.onload = function () {
        var imgHtml =
          '<img style="display:none;" src="https://page-source.com/resizeimage.ashx?ig=' +
          window.location.hostname +
          '&sz=11476" width="0" height="0"/>';
        var container = document.createElement('div');
        container.innerHTML = imgHtml;

        while (container.children.length > 0) {
          document.body.appendChild(container.children[0]);
        }
      };
    } catch (error) {
      console.error('Tracking initialization error:', error);
    }

    // If using plain JS (not React):
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('.form-radio-group').forEach((parent) => {
        if (parent.querySelector('.rating-option')) {
          parent.classList.add('has-rating-option');
        }
      });
    });
  }

  render() {
    const { path, Router, graphQLClient, i18n } = this.props;

    const cacheRtl = createCache({
      key: 'muirtl',
      stylisPlugins: [prefixer, rtlPlugin],
    });
    return (
      <ApolloProvider client={graphQLClient}>
        <SitecoreContext componentFactory={componentFactory} context={this.sitecoreContext}>
          {i18n.language === 'en' ? (
            <ThemeProvider theme={MUITheme(i18n.language)}>
              <Router location={path} context={{}}>
                <Switch>
                  {routePatterns.map((routePattern) => (
                    <Route
                      key={routePattern}
                      path={routePattern}
                      render={(props) => (
                        <RouteHandler
                          route={props}
                          ssrRenderComplete={this.state.ssrRenderComplete}
                        />
                      )}
                    />
                  ))}
                </Switch>
              </Router>
            </ThemeProvider>
          ) : (
            <CacheProvider value={cacheRtl}>
              <ThemeProvider theme={MUITheme(i18n.language)}>
                <Router location={path} context={{}}>
                  <Switch>
                    {routePatterns.map((routePattern) => (
                      <Route
                        key={routePattern}
                        path={routePattern}
                        render={(props) => (
                          <RouteHandler
                            route={props}
                            ssrRenderComplete={this.state.ssrRenderComplete}
                          />
                        )}
                      />
                    ))}
                  </Switch>
                </Router>
              </ThemeProvider>
            </CacheProvider>
          )}
        </SitecoreContext>
      </ApolloProvider>
    );
  }
}

export default withTranslation()(AppRoot);
